import React, {
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useMapControl } from "../contexts/MapControlContext";
import {
  createBoundsFromCenterMeters,
  getPrintLayout,
  type MapPrintOptions,
} from "../utils/printLayout";
import {
  type CircleParams,
  type DrawCreatedEvent,
  type GeoSpaceChiban,
  type GeoSpacePolygonItem,
  type GeoSpacePolygonCoordinates,
  type MarkerState,
  type PolygonParams,
  type PropertySelectionRow,
  type RealEstateSearchResult,
  type SavedMapPlotRequestDetail,
} from "../types";
import { type SavedMapPlotDataWithRequestId } from "../types/component";
import { type RealEstateRequestModalHandler } from "../types/realEstateRequest";
import {
  FeatureGroup,
  MapContainer,
  Rectangle,
  Popup,
  TileLayer,
  useMap,
  ImageOverlay,
  ScaleControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, {
  LatLng,
  type Layer,
  type Map,
  type Popup as LeafletPopup,
  type TileLayer as LeafletTileLayer,
  type LatLngBoundsExpression,
  type LatLngBounds,
} from "leaflet";
import { createMarkerEventHandlers } from "./markerEventHandlers";
import { CanvasPin } from "./CanvasPin";
import { ListedPropertyPinsLayer } from "./layers/ListedPropertyPinsLayer";
import { PIN_DRAW_LEVEL } from "../utils/canvasPin/CanvasPinMarker";
import { getSharedCanvasPinRenderer } from "../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getPlainClickPinIcon,
  getTeardropPinIcon,
} from "../utils/canvasPin/teardropPin";
import { calcPopupMaxHeight } from "../utils/popupAutoPan";
import "leaflet-draw/dist/leaflet.draw.css";
import "./styles/leaflet-attribution.css";
import "./styles/leaflet-tooltip.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import {
  GEOSPACE_CHIBAN_TILE_LAYER_NAME,
  LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME,
  CommonChibanTileLayer,
} from "@/features/map/components/CommonChibanTileLayer";
import { Button, ButtonVariantOption } from "@/components/Button";
import { Box, Modal } from "@mui/material";
import {
  AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION,
  AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA,
  AreaUsePurposeLayerControlWrapper,
} from "./AreaUsePurposeLayerControlWrapper";
import {
  NATIONAL_LAND_NUMERICAL_INFORMATION_HOST,
  CITY_PLANNING_DECISION_GIS_DATA_HOST,
} from "./mapLibre/layers/AreaUsePurposeTileLayer";
import { EditControl } from "react-leaflet-draw";
import { useZenrinOAuthToken } from "../hooks/useZenrinOAuthToken";
import { type BookTypeEng } from "@/types/acquirebook";
import { type Id, toast } from "react-toastify";
import {
  consumeZenrinRateLimitError,
  isZenrinRateLimitError,
} from "@/features/map/utils/zenrinRateLimit";
import { formatNumberWithComma } from "@/utils/formatNumber";
import "leaflet-geometryutil";
import { RangeSelectResultModal } from "./RangeSelectResultModal";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import {
  ZenrinTileLayer,
  ZENRIN_RESIDENTIAL_MIN_ZOOM,
  ZENRIN_RESIDENTIAL_MAX_ZOOM,
  ZENRIN_RESIDENTIAL_MAP_LAYER_NAME,
} from "@/features/map/components/ZenrinTileLayer";
import {
  ZenrinBlueMapTileLayer,
  ZENRIN_BLUE_MAP_MIN_ZOOM,
  ZENRIN_BLUE_MAP_LAYER_NAME,
} from "@/features/map/components/layers/ZenrinBlueMapTileLayer";
import { useFeatureFlags } from "@/configs/featureFlag";
import { extractGeospaceBounds, skipSelectChiban } from "../utils";
import {
  readableDistance,
  readableArea,
} from "@/features/map/utils/readableSize";
import { LegendsControl } from "./Legend";
import { CustomControl } from "./CustomControl";
import { FitToObjectsControl } from "./FitToObjectsControl";
import { CurrentLocationControl } from "./CurrentLocationControl";
import { LayerGroupControl, type OverlayGroup } from "./LayerGroupControl";
import {
  getOverlayGroups,
  GEOSPACE_ELECTRONIC_MAP_LAYER_NAME,
  GEOSPACE_SECTION_TITLE,
  ZENRIN_SECTION_TITLE,
  RECEPTION_BOOK_LAYER_NAME,
  ZENRIN_FETCHED_IMAGE_LAYER_NAME,
} from "../constants/overlayGroups";
import {
  TrustartChibanTileLayer,
  TRUSTART_CHIBAN_LAYER_NAME,
} from "@/features/map/components/layers/TrustartChibanTileLayer";
import {
  SeamlessPhotoTileLayer,
  SEAMLESS_PHOTO_LAYER_NAME,
} from "@/features/map/components/layers/SeamlessPhotoTileLayer";
import { DatedPhotoLayers } from "@/features/map/components/layers/DatedPhotoLayers";
import { PhotoCoverageNotice } from "@/features/map/components/PhotoCoverageNotice";
import {
  buildPhotoCoverageBadges,
  buildPhotoCoverageNoticeItems,
  type PhotoCoverageMap,
} from "@/features/map/utils/photoCoverage";
import type { TileCoverageStatus } from "@/features/map/utils/tileCoverage";
import { useNendoPhotoYearAvailability } from "@/features/map/hooks/useNendoPhotoYearAvailability";
import {
  GSI_HISTORICAL_PHOTO_LAYERS,
  NENDO_PHOTO_LAYER_NAME,
  pickNendoPhotoYearVisibility,
} from "@/features/map/constants/gsiPhotoLayers";
import { useTrustartTile } from "../hooks/useTrustartTile";
import { useRegistrationMapTile } from "../hooks/useRegistrationMapTile";
import {
  REGISTRATION_MAP_LAYER_NAME,
  RegistrationMapTileLayer,
} from "./mapLibre/layers/RegistrationMapTileLayer";
import { type SearchChainEntry } from "../utils/buildSearchChain";
import { isOverlayToggleable } from "../utils/isOverlayToggleable";
import { pickRangeSearchSource } from "../utils/pickRangeSearchSource";
import {
  useParcelSearchSources,
  SHAPE_CAPABLE_SOURCES,
  type ShapeSearchResult,
} from "../hooks/useParcelSearchSources";
import {
  SEARCH_SOURCE_LABEL,
  RANGE_SEARCH_RESULT_LIMIT,
} from "../utils/searchSourceMeta";
import { SEARCH_SOURCE_LAYER } from "../utils/searchSourceLayers";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ParcelPolygon } from "./ParcelPolygon";
import {
  FLOOD_10_LAYER_NAME,
  HazardFlood10Layer,
} from "./layers/HazardFlood10Layer";
import {
  FLOOD_20_LAYER_NAME,
  HazardFlood20Layer,
} from "./layers/HazardFlood20Layer";
import {
  FLOOD_30_LAYER_NAME,
  HazardFlood30Layer,
} from "./layers/HazardFlood30Layer";
import {
  HazardLandslideDebrisFlowLayer,
  HazardLandslideLandslideLayer,
  HazardLandslideSteepSlopeLayer,
  LANDSLIDE_DEBRIS_FLOW_LAYER_NAME,
  LANDSLIDE_LANDSLIDE_LAYER_NAME,
  LANDSLIDE_STEEP_SLOPE_LAYER_NAME,
} from "./layers/HazardLandslideLayer";
import {
  HazardTsunamiLayer,
  TSUNAMI_LAYER_NAME,
} from "./layers/HazardTsunamiLayer";
import {
  HazardStormSurgeLayer,
  STORM_SURGE_LAYER_NAME,
} from "./layers/HazardStormSurgeLayer";
import {
  LandslidePreventionLayer,
  LANDSLIDE_PREVENTION_LAYER_NAME,
} from "./layers/LandslidePreventionLayer";
import {
  LAND_PRICE_LAYER_NAME,
  LandPriceLayerControlWrapper,
} from "@/features/map/components/LandPriceLayerControlWrapper";
import { MaplibreLayerContext } from "@/features/map/components/mapLibre/context/MaplibreLayerContext";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { MAP_BORDER } from "@/features/map/constants/controlSize";
import {
  TRADE_PRICE_LAYER_NAME,
  TradePriceLayerControlWrapper,
} from "@/features/map/components/TradePriceLayerControlWrapper";
import { type TradePriceTileFilter } from "@/features/map/types/tradePrice";
import {
  REAL_ESTATE_SEARCH_LAYER_NAME,
  RealEstateSearchLayer,
} from "./layers/RealEstateSearchLayer";
import { MapAttributionControl } from "@/features/map/components/MapAttributionControl";
import { MapCenterControl } from "./MapCenterControl";
import {
  REQUESTED_PICTURES_PLOT_LAYER_NAME,
  RequestedPicturesPlotLayer,
} from "./layers/RequestedPicturesPlotLayer";
import {
  IdolFarmlandLayer,
  IDOL_FARMLAND_LAYER_NAME,
} from "./layers/IdolFarmlandLayer";
import { FarmlandLayer, FARMLAND_LAYER_NAME } from "./layers/FarmlandLayer";
import { CFarmlandFilterDialog } from "@/features/map/components/farmland/FarmlandFilterDialog";
import {
  CityPlanningAreaLayer,
  CITY_PLANNING_AREA_LAYER_NAME,
} from "./layers/CityPlanningAreaLayer";
import {
  isRoadLayerMode,
  ROAD_LAYER_NAME,
  ROAD_LAYER_MODE_CLASS,
  ROAD_TILE_HOST,
  type RoadLayerMode,
} from "@/features/map/constants/roadLayer";
import { RoadLayer } from "./layers/RoadLayer";
import { createRoot } from "react-dom/client";
import { AreaSelectPopupContent } from "@/features/map/components/AreaSelectPopupContent";
import { CircleRadiusDisplay } from "@/features/map/components/CircleRadiusDisplay";
import { SavedPlotLayer, SAVED_PLOT_LAYER_NAME } from "./layers/SavedPlotLayer";
import {
  MunicipalDocumentLayer,
  MUNICIPAL_DOCUMENT_LAYER_NAME,
} from "./layers/MunicipalDocumentLayer";
import {
  ReceptionBookLayer,
  type ReceptionBookLayerRef,
} from "./layers/ReceptionBookLayer";
import { ZENRIN_BASE_URL } from "@/features/map/constants";
import { DEFAULT_FARMLAND_FILTER } from "@/features/map/constants/farmland";

import "../utils/leafletExtension.js";
import { type ChibanInput } from "@/types/kaokuNumber";
import { CdsTileLayerWithHook } from "@/features/map/components/CdsTileLayer";
import { type AuthorizeKeyResponse } from "@/features/map/api/getCdsMapAuthorizeKey";
import type { RealEstateSearchAddressResult } from "@/features/map/api/getRealEstateSearchAddress";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import TuneIcon from "@mui/icons-material/Tune";
import { type FarmlandFilterValues } from "@/features/map/types/farmland";
import { hasActiveFarmlandFilter } from "@/features/map/utils/farmlandFilter";
import {
  determineMapServicePermission,
  determineZenrinPermission,
} from "@/utils/determineMapServicePermission";
import {
  SchoolAreaLayer,
  SchoolType,
  ELEMENTARY_SCHOOL_AREA_LAYER_NAME,
  JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME,
  getEsSchoolAreaTileUrl,
  getJhSchoolAreaTileUrl,
  getEsSchoolAreaDataLayerName,
  getJhSchoolAreaDataLayerName,
} from "@/features/map/components/layers/SchoolAreaLayer";
import { type AddressIntegrator } from "@/utils/AddressIntegrator";
import YearSelectableLayer from "@/features/map/components/layers/YearSelectableLayer";
import {
  FIRE_PROTECTION_AREA_LAYER_NAME,
  FireProtectionAreaLayer,
} from "@/features/map/components/layers/FireProtectionAreaLayer";
import {
  HIGH_LEVEL_AREA_LAYER_NAME,
  HighLevelAreaLayer,
} from "@/features/map/components/layers/HighLevelAreaLayer";
import {
  HIGH_LEVEL_USE_AREA_LAYER_NAME,
  HighLevelUseAreaLayer,
} from "@/features/map/components/layers/HighLevelUseAreaLayer";
import { zenrinComputeA3Bounds } from "@/features/map/utils/zenrinComputeA3Bounds";
import {
  DISASTER_RISK_AREA_LAYER_NAME,
  DisasterRiskAreaLayer,
} from "@/features/map/components/layers/DisasterRiskAreaLayer";
import {
  DRAWN_SHAPES_PANE_NAME,
  CURRENT_LOCATION_PANE_NAME,
} from "../constants/pane";

const MAX_SEARCH_AREA_SQUARE_METERS = 160000;
// ゼンリンのポリゴン範囲検索はGEOSPACEより狭い上限（100000㎡）とする
const MAX_SEARCH_AREA_SQUARE_METERS_ZENRIN = 100000;
const MAX_SEARCH_RADIUS_METERS = 200;
const MAX_SEARCH_VERTEX = 10;

export const NO_INFORMATION_LAYER_NAME = "情報レイヤーなし";

// ピンのデフォルト画像を設定する
const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Leaflet Map の生成完了を親へ伝える橋渡しコンポーネント。
 * @param props - 親へ渡すためのプロパティ。
 * @param props.setMapInstance - 取得した Map インスタンスを親コンポーネントへ通知するコールバック。
 */
const MapInitializer: React.FC<{
  setMapInstance: (mapInstance: Map) => void;
}> = ({ setMapInstance }) => {
  const mapInstance = useMap();

  useEffect(() => {
    setMapInstance(mapInstance);
  }, [mapInstance, setMapInstance]);

  return null;
};

/**
 * 印刷出力で実際に切り出される範囲を、地図上に矩形として表示する。
 * 地図中心 + paperSize / orientation / scaleDenominator から bounds を算出し、
 * 地図移動・リサイズに追従する。
 */
const PrintFrameRectangle: React.FC<{
  printOptions: MapPrintOptions;
}> = ({ printOptions }) => {
  const mapInstance = useMap();
  const [bounds, setBounds] = useState<LatLngBounds | null>(null);

  useEffect(() => {
    if (!printOptions.scaleDenominator) {
      setBounds(null);
      return;
    }
    const layout = getPrintLayout(printOptions);
    const widthMeters =
      (layout.mapWidthMm / 1000) * printOptions.scaleDenominator;
    const heightMeters =
      (layout.mapHeightMm / 1000) * printOptions.scaleDenominator;
    const recompute = (): void => {
      const center = mapInstance.getCenter();
      setBounds(
        createBoundsFromCenterMeters(center, widthMeters, heightMeters)
      );
    };
    recompute();
    mapInstance.on("move", recompute);
    mapInstance.on("resize", recompute);
    return () => {
      mapInstance.off("move", recompute);
      mapInstance.off("resize", recompute);
    };
  }, [mapInstance, printOptions]);

  if (!bounds) return null;
  return (
    <Rectangle
      bounds={bounds}
      pathOptions={{
        color: "#2563eb",
        weight: 2,
        dashArray: "6 4",
        fill: false,
      }}
      interactive={false}
    />
  );
};

interface Props {
  map: Map | null;
  setMap: React.Dispatch<React.SetStateAction<Map | null>>;
  clickedAreaPolygonData: GeoSpacePolygonCoordinates | undefined;
  polygonData: GeoSpacePolygonItem[][][] | undefined;
  selectedPropertiesPinData: LatLng[];
  /** 物件一覧で選んでいる行の青ピンの地点。選択中の見た目で描く */
  focusedPropertyPinPosition?: LatLng | null;
  /** 物件一覧の青ピンを押した時 */
  onListedPropertyPinClick?: (
    position: LatLng,
    event: L.LeafletMouseEvent
  ) => void;
  viewLatLng: LatLng;
  setClickedLatLng: Dispatch<SetStateAction<LatLng | null>>;
  markerState: MarkerState;
  receptionBookMarkerStates?: MarkerState[];
  selectedProperties: PropertySelectionRow[];
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  buildLocationString: (r: GeoSpaceChiban) => string;
  checkDuplicateProperties: (
    rows: PropertySelectionRow[],
    bookType: BookTypeEng,
    prefName: string,
    location: string,
    chiban: string
  ) => boolean;
  findDuplicatePropertyIndex: (
    rows: PropertySelectionRow[],
    bookType: BookTypeEng,
    prefName: string,
    location: string,
    chiban: string
  ) => number;
  mergePropertySelectionRow: (
    rows: PropertySelectionRow[],
    index: number,
    area: number | null,
    rawData?: GeoSpaceChiban
  ) => PropertySelectionRow[] | undefined;
  setMarkerState: React.Dispatch<React.SetStateAction<MarkerState>>;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  realEstateSearchResult: RealEstateSearchResult[];
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  savedPlotData?: SavedMapPlotRequestDetail[];
  /** 物件リストID → ピンの色（複数のリストを出した時にリストごとに色を分ける） */
  savedPlotColorByRequestId?: ReadonlyMap<number, string>;
  savedMapPlotData?: SavedMapPlotDataWithRequestId | null;
  checkedLayers: string[];
  setCheckedLayers: React.Dispatch<React.SetStateAction<string[]>>;
  handlePinSave?: () => void;
  onTagsChange: (tags: string[]) => void;
  onRequestedPicturesTagsChange: (
    tags: string[],
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  onRequestedPicturesRemarksChange: (
    remarks: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  onRequestedPicturesAdditionalFieldsChange: (
    additionalFields: string,
    prefName: string,
    location: string,
    chiban: string
  ) => void;
  onMapClickTagsUpdate?: () => string[];
  onMapClickRemarksUpdate?: () => string;
  onMapClickAdditionalFieldsUpdate?: () => string;
  setCurrentPopupRowId: React.Dispatch<React.SetStateAction<number | null>>;
  currentPopupIdentifier?: {
    location: string;
    chiban: string;
    prefName: string;
  } | null;
  mapRef?: React.MutableRefObject<L.Map | null>;
  onRealEstateSearchTagsChange?: (index: number, tags: string[]) => void;
  onRealEstateSearchRemarksChange?: (index: number, remarks: string) => void;
  onRealEstateSearchAdditionalFieldsChange?: (
    index: number,
    additionalFields: string
  ) => void;
  onRealEstateSearchPinClick?: (index: number) => void;
  /** 不動産検索の土地のポップアップを開いた時に、住居表示を取る */
  loadRealEstateSearchLandAddress?: (
    item: RealEstateSearchResult
  ) => Promise<RealEstateSearchAddressResult>;
  onReceptionBookTagsChange?: (rowId: number, tags: string[]) => void;
  onReceptionBookRemarksChange?: (rowId: number, remarks: string) => void;
  onReceptionBookAdditionalFieldsChange?: (
    rowId: number,
    additionalFields: string
  ) => void;
  crm?: boolean;
  availableFields?: AdditionalFieldConfig;
  hasAvailableFields?: boolean;
  zenrinImageSelectionMode?: boolean;
  onZenrinSelectionBoundsChange?: (bounds: LatLngBounds | null) => void;
  zenrinImageOverlay?: {
    bounds: LatLngBoundsExpression;
    url: string;
  } | null;
  hasMapViewingPermission: boolean | null;
  // 地番検索
  kaokuNumberSearchFlag?: boolean;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler;
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler;
  // 物件概要書
  propertySummary?: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
  // overlayのON/OFFを外部からできるようにする
  registerOverlayController?: (controller: OverlayController | null) => void;
  /**
   * 地番検索に使うデータソースの優先順付きの列（ChibanViewer の buildSearchChain の結果）。
   * 範囲選択検索も単一検索と同じ列からソースを選ぶため、叩く条件を親と共有する。
   */
  searchChain: SearchChainEntry[];
  // 取引価格タイルフィルター
  tradePriceFilter?: TradePriceTileFilter;
  onTradePriceFilterChange?: (filter: TradePriceTileFilter) => void;
  onShowTradePriceDetailList?: (location: {
    prefecture: string;
    municipality: string;
    districtName: string;
  }) => void;
  /**
   * 印刷出力で実際に切り出される範囲を、地図上に矩形として表示する。
   * 値があれば現在の地図中心から計算した bounds に追従して描画する。
   */
  printFramePreview?: MapPrintOptions | null;
  /**
   * 地図出力パネルを開いている間 true。
   * 出力プレビュー/ダウンロードはゼンリン関連レイヤーの状態に依存するため、
   * 開いている間は ZENRIN / GEOSPACE セクションの操作を抑止する。
   */
  mapExportPanelOpen?: boolean;
  /**
   * 受付帳ポップアップを外部から開くためのコールバック
   * ChibanViewer から openPopupById を呼べるようにするため
   */
  onRegisterOpenReceptionBookPopup?: (fn: (id: number) => void) => void;
}

/** レイヤーの表示状態を LeafletMap の外から操作するためのコントローラー。 */
export interface OverlayController {
  setOverlay: (name: string, visible: boolean) => void;
  /**
   * そのレイヤーを今すぐ ON にでき、実際に描画されるか。
   * レイヤーパネルに無い・操作不可（disabled / locked）・タイル未取得のレイヤーは false。
   */
  canShowOverlay: (name: string) => boolean;
}

export const INITIAL_ZOOM = 17;
// 過度なズームアウトを防止し、日本全体が見える程度に制限するため最小ズームレベルを7に設定
export const MIN_ZOOM = 7;
export const MAX_ZOOM = 22;
export const OPEN_STREET_MAP_MAX_NATIVE_ZOOM = 19;

// 排他制御（GEOSPACEとゼンリンは同時に表示できない仕様）のためレイヤー名をグループ化
const GEOSPACE_LAYER_NAMES = [
  GEOSPACE_ELECTRONIC_MAP_LAYER_NAME,
  GEOSPACE_CHIBAN_TILE_LAYER_NAME,
  LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME,
];
const ZENRIN_LAYER_NAMES = [
  ZENRIN_RESIDENTIAL_MAP_LAYER_NAME,
  ZENRIN_BLUE_MAP_LAYER_NAME,
  ZENRIN_FETCHED_IMAGE_LAYER_NAME,
];

/**
 * 両レイヤーの同時表示による地図の重なりを防ぐため、一方をONにしたら他方を自動的にOFFにする排他制御関数
 * @param visibility 現在のレイヤー表示状態
 * @param prioritizedGroup 優先するグループ（新しくONにしたグループを指定）
 */
const enforceExclusiveOverlayVisibility = (
  visibility: Record<string, boolean>,
  prioritizedGroup: "geospace" | "zenrin" = "geospace"
): Record<string, boolean> => {
  const nextVisibility = { ...visibility };
  const geospaceActive = GEOSPACE_LAYER_NAMES.some(
    (name) => nextVisibility[name]
  );
  const zenrinActive = ZENRIN_LAYER_NAMES.some((name) => nextVisibility[name]);

  // 両方のグループがアクティブな場合、優先されない方をOFFにする
  if (geospaceActive && zenrinActive) {
    const namesToTurnOff =
      prioritizedGroup === "geospace"
        ? ZENRIN_LAYER_NAMES
        : GEOSPACE_LAYER_NAMES;
    namesToTurnOff.forEach((name) => {
      if (nextVisibility[name]) {
        nextVisibility[name] = false;
      }
    });
  }

  return nextVisibility;
};

export const LeafletMap: React.FC<Props> = ({
  map,
  setMap,
  clickedAreaPolygonData,
  polygonData,
  selectedPropertiesPinData,
  focusedPropertyPinPosition = null,
  onListedPropertyPinClick,
  viewLatLng,
  setClickedLatLng,
  markerState,
  receptionBookMarkerStates,
  selectedProperties,
  setSelectedProperties,
  checkDuplicateProperties,
  findDuplicatePropertyIndex,
  mergePropertySelectionRow,
  buildLocationString,
  setMarkerState,
  setZoom,
  realEstateSearchResult,
  handleKaokuNumberSearch,
  savedPlotData,
  savedPlotColorByRequestId,
  savedMapPlotData,
  checkedLayers,
  setCheckedLayers,
  handlePinSave,
  onRequestedPicturesTagsChange,
  onRequestedPicturesRemarksChange,
  onRequestedPicturesAdditionalFieldsChange,
  onMapClickTagsUpdate,
  onMapClickRemarksUpdate,
  onMapClickAdditionalFieldsUpdate,
  setCurrentPopupRowId,
  currentPopupIdentifier,
  mapRef: externalMapRef,
  onRealEstateSearchTagsChange,
  onRealEstateSearchRemarksChange,
  onRealEstateSearchAdditionalFieldsChange,
  onRealEstateSearchPinClick,
  loadRealEstateSearchLandAddress,
  onReceptionBookTagsChange,
  onReceptionBookRemarksChange,
  onReceptionBookAdditionalFieldsChange,
  crm = false,
  availableFields,
  hasAvailableFields,
  zenrinImageSelectionMode = false,
  onZenrinSelectionBoundsChange,
  zenrinImageOverlay,
  hasMapViewingPermission,
  kaokuNumberSearchFlag = false,
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal,
  handleRealEstateResearchRequestModal,
  propertySummary = false,
  handleNewPropertySheet,
  registerOverlayController,
  searchChain,
  tradePriceFilter,
  onTradePriceFilterChange,
  onShowTradePriceDetailList,
  printFramePreview,
  mapExportPanelOpen,
  onRegisterOpenReceptionBookPopup,
}) => {
  // geospace/zenrinモードの切り替えにより範囲検索時のAPI呼び出し先を変更するためmapModeを取得
  const { mapMode } = useMapControl();

  // イベントハンドラ内でのクロージャ問題（古いmapMode値を参照）を回避するためrefで最新値を追跡
  const mapModeRef = useRef(mapMode);
  useEffect(() => {
    mapModeRef.current = mapMode;
  }, [mapMode]);

  // 地図領域の境界を視覚的に明確にするためボーダーを追加
  // 高さは親（ChibanViewer の地図領域）に合わせる
  const containerStyle: CSSProperties = {
    height: "100%",
    width: "100%",
    display: "block",
    border: MAP_BORDER,
    boxSizing: "border-box",
  };

  // 地図の高さは画面のレイアウト（ツールバーや読み込んだリストの行の有無）で変わる。
  // Leaflet はウィンドウのリサイズしか見ないため、枠の大きさが変わったら測り直す
  useEffect(() => {
    if (!map || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      map.invalidateSize({ debounceMoveend: true });
    });
    observer.observe(map.getContainer());
    return () => {
      observer.disconnect();
    };
  }, [map]);

  const popupRef = useRef<LeafletPopup | null>(null);
  // 標準地図のopenStreetMapを無効にするフラグ
  const [disableOpenStreetMap, setDisableOpenStreetMap] =
    useState<boolean>(false);
  const osmLayerRef = useRef<LeafletTileLayer>(null);
  const [geospaceLatLngBounds, setGeospaceLatLngBounds] =
    useState<LatLngBounds | null>(null);

  // GEOSPACE/ゼンリン排他制御のためoverlayVisibility stateで全レイヤーの可視性を一元管理
  const [overlayVisibility, setOverlayVisibility] = useState<
    Record<string, boolean>
  >(() =>
    enforceExclusiveOverlayVisibility({
      [GEOSPACE_ELECTRONIC_MAP_LAYER_NAME]: checkedLayers.includes(
        GEOSPACE_ELECTRONIC_MAP_LAYER_NAME
      ),
      [GEOSPACE_CHIBAN_TILE_LAYER_NAME]: checkedLayers.includes(
        GEOSPACE_CHIBAN_TILE_LAYER_NAME
      ),
      [LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME]: checkedLayers.includes(
        LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME
      ),
      // TRUSTART は排他リスト外のため、GEOSPACE/ゼンリンと独立して ON/OFF できる
      [TRUSTART_CHIBAN_LAYER_NAME]: checkedLayers.includes(
        TRUSTART_CHIBAN_LAYER_NAME
      ),
      [REGISTRATION_MAP_LAYER_NAME]: checkedLayers.includes(
        REGISTRATION_MAP_LAYER_NAME
      ),
      [SEAMLESS_PHOTO_LAYER_NAME]: checkedLayers.includes(
        SEAMLESS_PHOTO_LAYER_NAME
      ),
      // 年代別の写真（国土地理院）
      [NENDO_PHOTO_LAYER_NAME]: checkedLayers.includes(NENDO_PHOTO_LAYER_NAME),
      // 年度別空中写真の年度キー（`名前_2024` 等）。この初期値に載せないと、
      // 直後の checkedLayers 同期（overlayVisibility の true キーで上書き）で
      // 選択中の年度が落ち、「親は ON なのにタイルもチェックも出ない」状態になる
      ...pickNendoPhotoYearVisibility(checkedLayers),
      ...Object.fromEntries(
        GSI_HISTORICAL_PHOTO_LAYERS.map((layer) => [
          layer.name,
          checkedLayers.includes(layer.name),
        ])
      ),
      [FLOOD_10_LAYER_NAME]: checkedLayers.includes(FLOOD_10_LAYER_NAME),
      [FLOOD_20_LAYER_NAME]: checkedLayers.includes(FLOOD_20_LAYER_NAME),
      [FLOOD_30_LAYER_NAME]: checkedLayers.includes(FLOOD_30_LAYER_NAME),
      [TSUNAMI_LAYER_NAME]: checkedLayers.includes(TSUNAMI_LAYER_NAME),
      [STORM_SURGE_LAYER_NAME]: checkedLayers.includes(STORM_SURGE_LAYER_NAME),
      [LANDSLIDE_STEEP_SLOPE_LAYER_NAME]: checkedLayers.includes(
        LANDSLIDE_STEEP_SLOPE_LAYER_NAME
      ),
      [LANDSLIDE_DEBRIS_FLOW_LAYER_NAME]: checkedLayers.includes(
        LANDSLIDE_DEBRIS_FLOW_LAYER_NAME
      ),
      [LANDSLIDE_LANDSLIDE_LAYER_NAME]: checkedLayers.includes(
        LANDSLIDE_LANDSLIDE_LAYER_NAME
      ),
      [LANDSLIDE_PREVENTION_LAYER_NAME]: checkedLayers.includes(
        LANDSLIDE_PREVENTION_LAYER_NAME
      ),
      [SAVED_PLOT_LAYER_NAME]: checkedLayers.includes(SAVED_PLOT_LAYER_NAME),
      [MUNICIPAL_DOCUMENT_LAYER_NAME]: checkedLayers.includes(
        MUNICIPAL_DOCUMENT_LAYER_NAME
      ),
      [REQUESTED_PICTURES_PLOT_LAYER_NAME]: checkedLayers.includes(
        REQUESTED_PICTURES_PLOT_LAYER_NAME
      ),
      [REAL_ESTATE_SEARCH_LAYER_NAME]: checkedLayers.includes(
        REAL_ESTATE_SEARCH_LAYER_NAME
      ),
      [IDOL_FARMLAND_LAYER_NAME]: checkedLayers.includes(
        IDOL_FARMLAND_LAYER_NAME
      ),
      [FARMLAND_LAYER_NAME]: checkedLayers.includes(FARMLAND_LAYER_NAME),
      [CITY_PLANNING_AREA_LAYER_NAME]: checkedLayers.includes(
        CITY_PLANNING_AREA_LAYER_NAME
      ),
      [FIRE_PROTECTION_AREA_LAYER_NAME]: checkedLayers.includes(
        FIRE_PROTECTION_AREA_LAYER_NAME
      ),
      [HIGH_LEVEL_AREA_LAYER_NAME]: checkedLayers.includes(
        HIGH_LEVEL_AREA_LAYER_NAME
      ),
      [HIGH_LEVEL_USE_AREA_LAYER_NAME]: checkedLayers.includes(
        HIGH_LEVEL_USE_AREA_LAYER_NAME
      ),
      [ROAD_LAYER_NAME]: checkedLayers.includes(ROAD_LAYER_NAME),
      [DISASTER_RISK_AREA_LAYER_NAME]: checkedLayers.includes(
        DISASTER_RISK_AREA_LAYER_NAME
      ),
      [ELEMENTARY_SCHOOL_AREA_LAYER_NAME]: checkedLayers.includes(
        ELEMENTARY_SCHOOL_AREA_LAYER_NAME
      ),
      [JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME]: checkedLayers.includes(
        JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME
      ),
      [ZENRIN_RESIDENTIAL_MAP_LAYER_NAME]: checkedLayers.includes(
        ZENRIN_RESIDENTIAL_MAP_LAYER_NAME
      ),
      [ZENRIN_BLUE_MAP_LAYER_NAME]: checkedLayers.includes(
        ZENRIN_BLUE_MAP_LAYER_NAME
      ),
      [ZENRIN_FETCHED_IMAGE_LAYER_NAME]: checkedLayers.includes(
        ZENRIN_FETCHED_IMAGE_LAYER_NAME
      ),
      [RECEPTION_BOOK_LAYER_NAME]: false,
      [AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION]:
        checkedLayers.includes(
          AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION
        ),
      [AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA]:
        checkedLayers.includes(
          AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA
        ),
      [LAND_PRICE_LAYER_NAME]: checkedLayers.includes(LAND_PRICE_LAYER_NAME),
      [TRADE_PRICE_LAYER_NAME]: checkedLayers.includes(TRADE_PRICE_LAYER_NAME),
    })
  );
  const [roadLayerMode, setRoadLayerMode] = useState<RoadLayerMode>(
    ROAD_LAYER_MODE_CLASS
  );
  const layerControlChoiceValues = useMemo(
    () => ({ [ROAD_LAYER_NAME]: roadLayerMode }),
    [roadLayerMode]
  );
  const handleSelectLayerControlChoice = useCallback(
    (name: string, value: string) => {
      if (name === ROAD_LAYER_NAME && isRoadLayerMode(value)) {
        setRoadLayerMode(value);
      }
    },
    []
  );
  const [farmlandFilter, setFarmlandFilter] = useState<FarmlandFilterValues>(
    DEFAULT_FARMLAND_FILTER
  );
  const [farmlandFilterDialogOpen, setFarmlandFilterDialogOpen] =
    useState(false);
  const farmlandFilterActive = hasActiveFarmlandFilter(farmlandFilter);

  // 画面中央にA3サイズの範囲を表示してユーザーが取得範囲を確認できるようにするための矩形state
  const [zenrinSelectionBounds, setZenrinSelectionBounds] =
    useState<LatLngBounds | null>(null);

  // ReceptionBookLayerのref（ポップアップを開くために使用）
  const receptionBookLayerRef = useRef<ReceptionBookLayerRef>(null);

  // 契約期間・トライアル期間を考慮した権限判定を行うためlocalStorageから認証情報を取得
  const mapServiceAppId = localStorage.getItem("mapServiceAppId");
  const mapServiceContractStartDate = localStorage.getItem(
    "mapServiceContractStartDate"
  );
  const mapServiceContractEndDate = localStorage.getItem(
    "mapServiceContractEndDate"
  );
  const mapServiceTrialStartDate = localStorage.getItem(
    "mapServiceTrialStartDate"
  );
  const mapServiceTrialEndDate = localStorage.getItem("mapServiceTrialEndDate");

  // 契約期間またはトライアル期間内かどうかでゼンリン地図の利用可否を判定
  const canUseZenrinMap = determineZenrinPermission({
    contractStartDate: localStorage.getItem("mapZenrinContractStartDate"),
    contractEndDate: localStorage.getItem("mapZenrinContractEndDate"),
    trialStartDate: localStorage.getItem("mapZenrinTrialStartDate"),
    trialEndDate: localStorage.getItem("mapZenrinTrialEndDate"),
  });

  // appIdの存在と契約期間またはトライアル期間内かどうかでGEOSPACE地図の利用可否を判定
  const hasGeospacePermission = determineMapServicePermission({
    appId: mapServiceAppId,
    contractStartDate: mapServiceContractStartDate,
    contractEndDate: mapServiceContractEndDate,
    trialStartDate: mapServiceTrialStartDate,
    trialEndDate: mapServiceTrialEndDate,
  });
  const hasZenrinPermission = Boolean(canUseZenrinMap);

  // ゼンリン地図タイルとブルーマップでトークンを共有するためuseZenrinOAuthTokenフックで一元管理
  const { token: zenrinOAuthToken, refreshToken: refreshZenrinToken } =
    useZenrinOAuthToken(canUseZenrinMap);

  // トークンの再取得が連続で発生しないようにするためのref
  const isRefreshingZenrinTokenRef = useRef(false);

  const handleZenrinTokenInvalid = useCallback(() => {
    // 既にリフレッシュ中の場合はスキップ
    if (isRefreshingZenrinTokenRef.current) {
      return;
    }
    isRefreshingZenrinTokenRef.current = true;

    console.warn("Zenrin OAuth token is invalid, refreshing...");
    void refreshZenrinToken().finally(() => {
      // リフレッシュ完了後、少し待ってからフラグをリセット
      setTimeout(() => {
        isRefreshingZenrinTokenRef.current = false;
      }, 1000);
    });
  }, [refreshZenrinToken]);

  // 選択時に地図の表示範囲をGEOSPACEの契約エリアに制限するための判定フラグ
  const isGeospaceElectronicMapSelected =
    overlayVisibility[GEOSPACE_ELECTRONIC_MAP_LAYER_NAME] ?? false;

  // GEOSPACE/ゼンリンの排他制御を適用しながらレイヤー表示を切り替えるハンドラ
  const handleToggleOverlay = useCallback(
    (name: string, next: boolean) => {
      setOverlayVisibility((prev) => {
        // 「取得した住宅地図（表札あり）」をONにしたとき、「住宅地図（表札なし）」もONにする
        const shouldEnableNameplateBase =
          next &&
          name === ZENRIN_FETCHED_IMAGE_LAYER_NAME &&
          !prev[ZENRIN_RESIDENTIAL_MAP_LAYER_NAME];
        // 「住宅地図（表札なし）」をOFFにしたとき、「取得した住宅地図（表札あり）」もOFFにする
        const shouldDisableNameplate =
          !next &&
          name === ZENRIN_RESIDENTIAL_MAP_LAYER_NAME &&
          prev[ZENRIN_FETCHED_IMAGE_LAYER_NAME];
        const updated = {
          ...prev,
          [name]: next,
          ...(shouldEnableNameplateBase && {
            [ZENRIN_RESIDENTIAL_MAP_LAYER_NAME]: true,
          }),
          ...(shouldDisableNameplate && {
            [ZENRIN_FETCHED_IMAGE_LAYER_NAME]: false,
          }),
        };
        if (next && GEOSPACE_LAYER_NAMES.includes(name)) {
          return enforceExclusiveOverlayVisibility(updated, "geospace");
        }
        if (next && ZENRIN_LAYER_NAMES.includes(name)) {
          return enforceExclusiveOverlayVisibility(updated, "zenrin");
        }
        return updated;
      });
    },
    [setOverlayVisibility]
  );

  // ChibanViewerからレイヤーを切り替えできるようにする処理
  // canShowOverlay は描画ごとに変わる状態（タイル URL・レイヤーパネル構成）を見るため、
  // コントローラーを再登録せずに最新の判定を呼べるよう ref 経由にする
  const canShowOverlayRef = useRef<(name: string) => boolean>(() => false);

  useEffect(() => {
    if (!registerOverlayController) return;

    registerOverlayController({
      setOverlay: (name: string, visible: boolean) => {
        handleToggleOverlay(name, visible);
      },
      canShowOverlay: (name: string) => canShowOverlayRef.current(name),
    });

    return () => {
      registerOverlayController(null);
    };
  }, [registerOverlayController, handleToggleOverlay]);

  // zenrinImageOverlayの設定/削除に応じてレイヤー表示を自動的に切り替え
  useEffect(() => {
    if (!zenrinImageOverlay) {
      setOverlayVisibility((prev) => ({
        ...prev,
        [ZENRIN_FETCHED_IMAGE_LAYER_NAME]: false,
      }));
      return;
    }

    setOverlayVisibility((prev) => ({
      ...prev,
      [ZENRIN_FETCHED_IMAGE_LAYER_NAME]: true,
    }));
  }, [zenrinImageOverlay]);

  // 写真レイヤー（年代別）の表示範囲にデータがあるかの判定結果。タイルの 404 から各レイヤーが通知する
  const [photoCoverage, setPhotoCoverage] = useState<PhotoCoverageMap>({});
  const handlePhotoCoverageChange = useCallback(
    (layerName: string, status: TileCoverageStatus | null): void => {
      setPhotoCoverage((prev) => {
        if (status === null) {
          if (!(layerName in prev)) return prev;
          return Object.fromEntries(
            Object.entries(prev).filter(([name]) => name !== layerName)
          );
        }
        if (prev[layerName] === status) return prev;
        return { ...prev, [layerName]: status };
      });
    },
    []
  );
  const photoCoverageNoticeItems = useMemo(
    () => buildPhotoCoverageNoticeItems(photoCoverage, overlayVisibility),
    [photoCoverage, overlayVisibility]
  );
  const photoCoverageBadges = useMemo(
    () => buildPhotoCoverageBadges(photoCoverage),
    [photoCoverage]
  );

  // 年度別空中写真の年度リストを開いている間だけ、地図中心付近に写真がある年度を調べて印を付ける
  const [nendoYearListExpanded, setNendoYearListExpanded] = useState(false);
  const nendoYearAvailability = useNendoPhotoYearAvailability({
    map,
    enabled: nendoYearListExpanded,
  });
  const layerControlYearAvailability = useMemo(
    () =>
      nendoYearAvailability
        ? { [NENDO_PHOTO_LAYER_NAME]: nendoYearAvailability }
        : undefined,
    [nendoYearAvailability]
  );
  const handleYearListToggle = useCallback(
    (name: string, expanded: boolean): void => {
      if (name === NENDO_PHOTO_LAYER_NAME) {
        setNendoYearListExpanded(expanded);
      }
    },
    []
  );

  // 親コンポーネントで凡例表示等を制御するため、内部状態の変更をcheckedLayersに同期
  useEffect(() => {
    const activeOverlays = Object.entries(overlayVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([name]) => name);

    setCheckedLayers(activeOverlays);
  }, [overlayVisibility, setCheckedLayers]);

  // イベントハンドラ（範囲検索など）が古いクロージャで overlayVisibility を参照するのを避けるため、
  // mapModeRef と同様に ref で最新のレイヤー可視状態を追跡する
  const overlayVisibilityRef = useRef(overlayVisibility);
  useEffect(() => {
    overlayVisibilityRef.current = overlayVisibility;
  }, [overlayVisibility]);

  // 範囲検索（EditControl がマウント時に一度だけ束縛するハンドラ）から最新の検索チェーンを読むための ref
  const searchChainRef = useRef(searchChain);
  useEffect(() => {
    searchChainRef.current = searchChain;
  }, [searchChain]);

  // ユーザーが取得範囲を視覚的に確認できるよう、地図の移動・ズームに追従してA3サイズの範囲を再計算
  useEffect(() => {
    const applyZenrinSelectionBounds = (bounds: LatLngBounds | null): void => {
      setZenrinSelectionBounds(bounds);
      onZenrinSelectionBoundsChange?.(bounds);
    };

    if (!map || !zenrinImageSelectionMode) {
      applyZenrinSelectionBounds(null);
      return;
    }

    // Zenrinの選択範囲を新しい地図中心座標で更新する処理
    const updateZenrinSelectionBounds = (): void => {
      applyZenrinSelectionBounds(zenrinComputeA3Bounds(map.getCenter()));
    };

    updateZenrinSelectionBounds();
    map.on("move", updateZenrinSelectionBounds);
    map.on("zoom", updateZenrinSelectionBounds);

    return () => {
      map.off("move", updateZenrinSelectionBounds);
      map.off("zoom", updateZenrinSelectionBounds);
      applyZenrinSelectionBounds(null);
    };
  }, [map, zenrinImageSelectionMode, onZenrinSelectionBoundsChange]);

  // 受付帳ポップアップを外部から開けるようにする
  useEffect(() => {
    onRegisterOpenReceptionBookPopup?.((id) => {
      receptionBookLayerRef.current?.openPopupById(id);
    });
  }, [onRegisterOpenReceptionBookPopup]);

  // 受付帳を地図に反映した時、受付帳ピンのレイヤーを表示にする（ピンは ReceptionBookLayer が描く）
  const hasReceptionBookMarkers = (receptionBookMarkerStates?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasReceptionBookMarkers) return;
    setOverlayVisibility((prev) => ({
      ...prev,
      [RECEPTION_BOOK_LAYER_NAME]: true,
    }));
  }, [hasReceptionBookMarkers]);

  // ---- ここからデバッグ用コード ----
  const [debugVisible, setDebugVisible] = useState<boolean>(false);

  interface DebugViewProps {
    visible: boolean;
  }

  const DebugView: React.FC<DebugViewProps> = ({ visible }) => {
    if (visible) {
      return (
        <Box sx={{ my: 1 }}>
          <Button
            label={"地図を東京へ移動"}
            variant={ButtonVariantOption.Outlined}
            onClick={() => {
              map?.setView(
                new LatLng(35.695530421125156, 139.78227391979706),
                13
              );
            }}
          />
        </Box>
      );
    }
    return null;
  };

  // コンソールで"debug()"と入力するとデバッグモードを有効にすることが可能です
  Object.assign(window, {
    debug: (): void => {
      console.log("debug ui on");
      setDebugVisible(true);

      // 現在のズームレベルをコンソールに表示する
      map?.on("zoomend", function () {
        console.log("ZoomLevel:", map?.getZoom());
      });
      // 中心の座標を表示
      map?.on("moveend", function () {
        console.log("中心座標", map?.getCenter());
      });
    },
  });
  // ---- デバッグ用コードここまで ----

  // 図形描画中はクリックイベントを無効化
  const isDrawingRef = useRef(false);
  const isInitialViewHandled = useRef(false);

  const handleMapSearchClick = useCallback(
    (latlng: LatLng): void => {
      // 地番 API のレスポンス待ちの間にピンが一瞬表示されるのを防ぐため、クリック直後にピンを非表示にする
      // 受付帳ピンと同一地番のクリック時は青ピンを表示せず受付帳ポップアップを開きたいため、レスポンスを待ってから表示判定する
      setMarkerState((prev: MarkerState) => ({
        ...prev,
        latlng,
        hide: true,
        content: null,
      }));
      setClickedLatLng(latlng);
    },
    [setClickedLatLng, setMarkerState]
  );

  const handleRegistrationParcelClick = useCallback(
    ({ lat, lng }: L.LatLngLiteral): void => {
      // 地図クリック側と同じく、範囲選択の描画中は地番検索しない
      if (isDrawingRef.current) return;
      handleMapSearchClick(new L.LatLng(lat, lng));
    },
    [handleMapSearchClick]
  );

  // mapが準備できたらイベントリスナーを登録
  useEffect(() => {
    if (!map) return;

    const handleClick = (event: L.LeafletMouseEvent): void => {
      if (isDrawingRef.current) {
        return; // 描画中は処理をスキップ
      }
      // イベントが既に他のハンドラーで処理されている場合はスキップ
      if (event.originalEvent?.defaultPrevented) {
        return;
      }
      handleMapSearchClick(event.latlng);
    };

    const handleDrawStart = (): void => {
      isDrawingRef.current = true;
    };

    const handleDrawStop = (): void => {
      isDrawingRef.current = false;
    };

    // 範囲検索ポップアップがどの経路（✕・地図外クリック・別図形の描画など）で
    // 閉じられても、実行中の範囲検索リクエストを中断する
    const handlePopupClose = (): void => {
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
    };

    // イベントリスナーを登録
    map.on(L.Draw.Event.DRAWSTART, handleDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, handleDrawStop);
    map.on("click", handleClick);
    map.on("popupclose", handlePopupClose);

    return () => {
      map.off("click", handleClick);
      map.off(L.Draw.Event.DRAWSTART, handleDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, handleDrawStop);
      map.off("popupclose", handlePopupClose);
    };
  }, [handleMapSearchClick, map]);

  // 描画した図形をダウンロードに含めるように新しいペインを作成する
  useEffect(() => {
    if (!map) return;
    if (map.getPane(DRAWN_SHAPES_PANE_NAME)) return;

    const drawnShapedPane = map.createPane(DRAWN_SHAPES_PANE_NAME);
    drawnShapedPane.classList.add("leaflet-drawn-shapes-pane");
    const overlayPane = map.getPane("overlayPane");
    if (overlayPane?.style.zIndex) {
      drawnShapedPane.style.zIndex = overlayPane.style.zIndex;
    }
  }, [map]);

  // 現在地マーカー・精度円を表示するためのペインを作成する
  useEffect(() => {
    if (!map) return;
    if (map.getPane(CURRENT_LOCATION_PANE_NAME)) return;

    const currentLocationPane = map.createPane(CURRENT_LOCATION_PANE_NAME);
    currentLocationPane.classList.add("leaflet-current-location-pane");
    const markerPane = map.getPane("markerPane");
    if (markerPane?.style.zIndex) {
      currentLocationPane.style.zIndex = markerPane.style.zIndex;
    }
  }, [map]);

  useEffect(() => {
    if (!map) return;

    if (isGeospaceElectronicMapSelected && geospaceLatLngBounds != null) {
      map.setMaxBounds(geospaceLatLngBounds);
    } else {
      map.setMaxBounds(null as unknown as LatLngBoundsExpression);
    }
  }, [map, isGeospaceElectronicMapSelected, geospaceLatLngBounds]);

  // 外部のmapRefと内部のmapを同期するrefコールバック
  const mapRefCallback = useCallback(
    (mapInstance: Map | null) => {
      if (externalMapRef) {
        externalMapRef.current = mapInstance;
      }
    },
    [externalMapRef]
  );

  // ズーム後に実行される。
  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = (): void => {
      setZoom(map.getZoom());
    };

    map.on("zoomend", handleZoomEnd);

    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, [map, setZoom]);

  // 検索でピンを地図の中央へ移動したか（検索時は表示座標とピンに同じ座標を同時に入れる）。
  // このときだけ、ポップアップの高さ制限とアニメーションなしの移動で中心ズレを防ぐ。
  // クリックで立てたピンや、不動産検索結果への移動は対象外。
  const isSearchCenteredPin = markerState.latlng?.equals(viewLatLng) ?? false;
  // 下の表示座標の effect より先に更新されるよう、ここで同期する
  const isSearchCenteredPinRef = useRef(isSearchCenteredPin);
  useEffect(() => {
    isSearchCenteredPinRef.current = isSearchCenteredPin;
  }, [isSearchCenteredPin]);

  // 表示座標のStateが更新された時に実行する
  // 初回はMapContainerで指定したcenter/zoomを維持し、2回目以降の変更のみ反映する
  useEffect(() => {
    if (!map) return;

    if (!isInitialViewHandled.current) {
      isInitialViewHandled.current = true;
      return;
    }

    // 検索でピンを中央へ移動するときは animate: false が必須。アニメーション付きの移動に
    // すると、直後に開くポップアップの autoPan 処理（Leaflet の Popup._adjustPan）が
    // 移動アニメーションを中断するため、検索した地点が中央まで到達せず途中で止まってしまう。
    // ポップアップを開かない移動（不動産検索結果など）は Leaflet 既定のアニメーションに任せる。
    map.setView(
      viewLatLng,
      OPEN_STREET_MAP_MAX_NATIVE_ZOOM,
      isSearchCenteredPinRef.current ? { animate: false } : undefined
    );
  }, [map, viewLatLng]);

  // ポップアップ設定が変更された時はポップアップを表示する
  useEffect(() => {
    if (
      map &&
      markerState.content &&
      markerState.latlng?.lat &&
      markerState.latlng.lng
    ) {
      popupRef.current?.setLatLng(markerState.latlng);
      popupRef.current?.openOn(map);
    }
  }, [map, markerState]);

  const {
    mapZenrin,
    kaokuNumberSearch,
    cdsMap,
    cityPlanningAreaUsePurposeLayer,
    publicFarmlandLayer: farmland,
    idolFarmland,
    trustartChibanMap,
    registrationMapLayer,
    roadLayer: isRoadLayerEnabled,
    reassistArchive,
  } = useFeatureFlags();

  // 役所地番地図レイヤーを利用できるテナントか（暫定: MunicipalParcelMapPricing 由来）
  const currentUser = useCurrentUser();
  const isMunicipalParcelMapAvailable =
    currentUser?.isMunicipalParcelMapAvailable ?? false;

  // 権限とフィーチャーフラグの両方を考慮してセクションの表示/非表示を決定
  const showGeospaceSection = hasGeospacePermission;
  const showZenrinSection = hasZenrinPermission && Boolean(mapZenrin);

  // 権限がないのに表示エラーになることを防ぐため、GEOSPACE権限がない場合はレイヤーを自動的にOFF
  useEffect(() => {
    if (showGeospaceSection) return;

    setOverlayVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      [
        GEOSPACE_ELECTRONIC_MAP_LAYER_NAME,
        GEOSPACE_CHIBAN_TILE_LAYER_NAME,
        LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME,
      ].forEach((name) => {
        if (next[name]) {
          next[name] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [showGeospaceSection]);

  // 権限がないのに表示エラーになることを防ぐため、ゼンリン権限がない場合はレイヤーを自動的にOFF
  useEffect(() => {
    if (showZenrinSection) return;

    setOverlayVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      [ZENRIN_RESIDENTIAL_MAP_LAYER_NAME, ZENRIN_BLUE_MAP_LAYER_NAME].forEach(
        (name) => {
          if (next[name]) {
            next[name] = false;
            changed = true;
          }
        }
      );
      return changed ? next : prev;
    });
  }, [showZenrinSection]);

  // 対象テナント（役所地番地図レイヤー利用可）は初期表示 ON にする
  // （フィーチャーフラグ ON 時のみ）。初回だけ適用し、その後のユーザー操作（OFF）は尊重する。
  // 暫定: 非対象テナントは disabled のため自動 ON しない。
  const didDefaultTrustartOnRef = useRef(false);
  useEffect(() => {
    if (didDefaultTrustartOnRef.current) return;
    if (!trustartChibanMap) return;
    if (!isMunicipalParcelMapAvailable) return;

    didDefaultTrustartOnRef.current = true;
    setOverlayVisibility((prev) =>
      prev[TRUSTART_CHIBAN_LAYER_NAME]
        ? prev
        : { ...prev, [TRUSTART_CHIBAN_LAYER_NAME]: true }
    );
  }, [trustartChibanMap, isMunicipalParcelMapAvailable]);

  // 利用可否が false（未契約・失効）に変わった場合は、チェック状態が ON のまま
  // 残らないよう役所地番地図・登記所備付地図レイヤーを OFF に戻す。
  useEffect(() => {
    if (isMunicipalParcelMapAvailable) return;
    setOverlayVisibility((prev) =>
      prev[TRUSTART_CHIBAN_LAYER_NAME] || prev[REGISTRATION_MAP_LAYER_NAME]
        ? {
            ...prev,
            [TRUSTART_CHIBAN_LAYER_NAME]: false,
            [REGISTRATION_MAP_LAYER_NAME]: false,
          }
        : prev
    );
  }, [isMunicipalParcelMapAvailable]);

  // ユーザーがポリゴンを地図に描画した際、範囲内の地番を取得する
  const drawnItemsRef = useRef(L.featureGroup());
  // 範囲検索は単一検索と同じチェーンからソースを 1 つ選び、その図形検索を呼ぶ
  const { runShapeSearch } = useParcelSearchSources();
  // TRUSTART地番図の統合タイルURL（フィーチャーフラグ ON かつ対象テナントのみ取得・失敗時はundefined）
  // 署名付きURL発行API自体もBE側で契約ガードするため、非対象では取得しない。
  const { pmtilesUrl: trustartPmtilesUrl } = useTrustartTile(
    Boolean(trustartChibanMap) && isMunicipalParcelMapAvailable
  );
  // 登記所備付地図も役所地番地図と同じく対象テナントのみ取得する。
  const canUseRegistrationMap =
    Boolean(registrationMapLayer) && isMunicipalParcelMapAvailable;
  const { pmtilesUrl: registrationMapPmtilesUrl } = useRegistrationMapTile(
    canUseRegistrationMap
  );
  // 登記所備付地図を出せるとき（フラグ ON・対象テナントかつタイル公開済み）だけ URL を持つ。
  // 乖離リカバリー・マウント・凡例はすべてこれを見る。
  const renderableRegistrationMapUrl = canUseRegistrationMap
    ? registrationMapPmtilesUrl
    : undefined;
  const canRenderRegistrationMap = renderableRegistrationMapUrl !== undefined;
  // パネルには、描画できるときに加えて、非対象テナントにもロック表示で出す
  // （役所地番地図と同じくお問い合わせ導線を見せる）。
  const showRegistrationMapInPanel =
    canRenderRegistrationMap ||
    (Boolean(registrationMapLayer) && !isMunicipalParcelMapAvailable);
  // 範囲検索で実際に使ったソースと Tier。結果一覧の出典表示・件数上限の説明の出し分け・
  // 非表示ソース（Tier 2）で引いたときの乖離リカバリーに使う。
  const [rangeSearchSource, setRangeSearchSource] = useState<
    SearchChainEntry | undefined
  >(undefined);
  const rangeSearchSourceId = rangeSearchSource?.id;

  const [geospaceReverseByShapeResult, setGeospaceReverseByShapeResult] =
    useState<GeoSpaceChiban[] | undefined>(undefined);
  // 範囲検索の結果が BE の返却上限で打ち切られたか（登記所備付地図のみ返す）
  const [rangeSearchTruncated, setRangeSearchTruncated] =
    useState<boolean>(false);

  const [toastId, setToastId] = useState<Id | null>(null);
  // 範囲検索の実行中リクエストを中断するためのAbortController。
  // ポップアップが閉じられたら（popupclose）このrefを介してabortする。
  const searchAbortRef = useRef<AbortController | null>(null);
  const [RangeSelectModalIsOpen, setRangeSelectModalIsOpen] =
    useState<boolean>(false);
  const [newProperty, setNewProperty] = useState<PropertySelectionRow[]>([]);

  const handleAuthorizeKeyResponse = useCallback(
    (response: AuthorizeKeyResponse): void => {
      const bounds = extractGeospaceBounds(response.geospaceCdsRegionBlocks);
      if (bounds == null) {
        setGeospaceLatLngBounds(null);
        return;
      }

      const nextBounds = L.latLngBounds(
        [bounds.latitudeSouth, bounds.longitudeWest],
        [bounds.latitudeNorth, bounds.longitudeEast]
      );

      setGeospaceLatLngBounds((prev) => {
        if (prev?.equals(nextBounds)) {
          return prev;
        }
        return nextBounds;
      });
    },
    [setGeospaceLatLngBounds]
  );

  const handleDrawResult = (
    shapeResult: ShapeSearchResult | undefined,
    layer: Layer,
    toastId: Id | null
  ): void => {
    if (shapeResult != null) {
      const { results: result, truncated } = shapeResult;
      const hasValidChiban = result.some(
        (item: GeoSpaceChiban) =>
          !/^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF a-zA-Z]+$/.test(
            item.chiban
          )
      );

      if (hasValidChiban) {
        setRangeSearchTruncated(truncated);
        setGeospaceReverseByShapeResult(result);
        drawnItemsRef.current.removeLayer(layer);
        setRangeSelectModalIsOpen(true);
      } else {
        toast.info("有効な地番が存在しませんでした。");
        drawnItemsRef.current.removeLayer(layer);
      }
    } else {
      drawnItemsRef.current.removeLayer(layer);
      toast.dismiss(toastId ?? undefined);
      toast.info("データが存在しませんでした");
    }
  };

  /**
   * 面積を表示するためのポップアップを生成する
   * @param shapeArea 表示したい面積（単位込み）
   * @param shapeRadius 表示したい半径（単位込み）
   * @param layer ポップアップの対象レイヤー（削除ボタンを押した時に削除されて欲しいレイヤー）
   * @param hideSearchButton ゼンリンモードではポリゴン検索が利用できないため、ポリゴン描画時は検索ボタンを非表示にするフラグ
   */
  const makeAreaPopup = (
    shapeArea: string,
    shapeRadius: string,
    layer: L.Layer,
    hideSearchButton = false
  ): L.Popup => {
    const popupHTML = document.createElement("div");
    createRoot(popupHTML).render(
      <AreaSelectPopupContent
        searchSelectedArea={searchSelectedArea}
        shapeArea={shapeArea}
        shapeRadius={shapeRadius}
        layer={layer}
        drawnItemsRef={drawnItemsRef}
        hideSearchButton={hideSearchButton}
      />
    );
    return L.popup({ content: popupHTML, minWidth: 250 });
  };

  /**
   * 面積を計測し、ポップアップを表示する
   * @param e DrawCreatedEvent
   */
  const handleDrawCreated = (e: DrawCreatedEvent): void => {
    const layer = e.layer as Layer;
    let shapeArea = "";
    let shapeRadius = "";

    if (layer instanceof L.Circle) {
      // 選択しているレイヤーが円の場合は円の面積を求める
      const radius = layer.getRadius();
      shapeArea = readableArea(radius * radius * Math.PI);
      shapeRadius = readableDistance(radius);

      // 円のレイヤーを表示する
      drawnItemsRef.current.addLayer(layer);

      // 面積を表示するポップアップを表示する
      const popup = makeAreaPopup(shapeArea, shapeRadius, layer, false);
      layer.bindPopup(popup).openPopup(map?.getCenter());
    } else {
      const layer = e.layer as L.Polygon;

      // 選択しているレイヤーが多角形の場合はgeodesicArea()で面積を求める
      const latLngs = layer.getLatLngs();
      const polygonCoordinates = (latLngs as L.LatLng[][])[0];
      shapeArea = readableArea(L.GeometryUtil.geodesicArea(polygonCoordinates));

      // 距離表示可能なポリゴンをここで新規生成して表示する
      const labeledPolygonLayer = new L.LabeledPolygon(
        layer.getLatLngs(),
        layer.options
      );
      drawnItemsRef.current.addLayer(labeledPolygonLayer);

      // ここで距離ラベルを表示、mapに表示した後である必要がある
      labeledPolygonLayer.drawLabels();

      // 面積を表示するポップアップを表示する
      const popup = makeAreaPopup(
        shapeArea,
        shapeRadius,
        labeledPolygonLayer,
        false
      );
      labeledPolygonLayer.bindPopup(popup).openPopup(map?.getCenter());

      // leaflet-drawが生成したポリゴンは不要なので削除
      layer.remove();
    }
  };

  // 円またはポリゴンを描画後「検索」ボタンを押した時に図形内の地番を検索する関数
  const searchSelectedArea = async (layer: Layer): Promise<void> => {
    // onCreatedはvoidの型を期待するため、関数内に非同期関数を定義
    const processLayer = async (): Promise<void> => {
      // 範囲検索も単一検索と同じチェーンを使う。ただし「範囲内の全筆」を返す性質上、
      // ヒット打ち切りではなく図形検索に対応した先頭 1 ソースだけを使う。
      // ref から最新のチェーンを読む（クロージャ問題を回避）。
      const chain = searchChainRef.current;
      const searchSource = pickRangeSearchSource({
        chain,
        shapeCapable: SHAPE_CAPABLE_SOURCES,
      });

      if (!searchSource) {
        drawnItemsRef.current.removeLayer(layer);
        toast.info("範囲検索に使えるデータがありません");
        return;
      }
      setRangeSearchSource(searchSource);

      // このリクエスト用のAbortControllerを用意しrefに保持する。
      // ポップアップが閉じられた時（popupclose）にこのrefを介してabortする。
      const controller = new AbortController();
      searchAbortRef.current = controller;
      const { signal } = controller;

      const fetchCircleData = async (params: CircleParams): Promise<void> => {
        const { radius, lat, lng } = params;
        const id = toast.info("選択範囲のデータを取得中...", {
          autoClose: false,
        });
        setToastId(id);

        let result: Awaited<ReturnType<typeof runShapeSearch>>;
        try {
          result = await runShapeSearch(
            searchSource.id,
            { kind: "circle", radius, lat, lng },
            signal
          );
        } catch (error) {
          // ゼンリンのレート制限(429)は「0件」と区別して通知し、描画した範囲も消す
          if (isZenrinRateLimitError(error)) {
            toast.dismiss(id);
            drawnItemsRef.current.removeLayer(layer);
          }
          if (consumeZenrinRateLimitError(error)) {
            return;
          }
          throw error;
        }

        // 途中でポップアップが閉じられ中断された場合は結果を反映しない
        if (signal.aborted) {
          toast.dismiss(id);
          toast.info("検索処理をキャンセルしました");
          return;
        }

        handleDrawResult(result, layer, id);

        toast.dismiss(id);
      };

      const fetchPolygonData = async (params: PolygonParams): Promise<void> => {
        const { polygon } = params;
        const id = toast.info("選択範囲のデータを取得中...", {
          autoClose: false,
        });
        setToastId(id);

        let result: Awaited<ReturnType<typeof runShapeSearch>>;
        try {
          result = await runShapeSearch(
            searchSource.id,
            { kind: "polygon", polygon },
            signal
          );
        } catch (error) {
          // ゼンリンのレート制限(429)は「0件」と区別して通知し、描画した範囲も消す
          if (isZenrinRateLimitError(error)) {
            toast.dismiss(id);
            drawnItemsRef.current.removeLayer(layer);
          }
          if (consumeZenrinRateLimitError(error)) {
            return;
          }
          throw error;
        }

        // 途中でポップアップが閉じられ中断された場合は結果を反映しない
        if (signal.aborted) {
          toast.dismiss(id);
          toast.info("検索処理をキャンセルしました");
          return;
        }

        handleDrawResult(result, layer, id);

        toast.dismiss(id);
      };

      try {
        if (layer instanceof L.Circle) {
          const { lat, lng } = layer.getLatLng();
          const radius = layer.getRadius();
          if (radius <= MAX_SEARCH_RADIUS_METERS) {
            await fetchCircleData({ radius, lat, lng });
          } else {
            drawnItemsRef.current.removeLayer(layer);
            toast.info(
              `円の半径は${formatNumberWithComma(
                MAX_SEARCH_RADIUS_METERS
              )}m以内で描画してください`
            );
          }
        } else {
          const polygonLayer = layer as L.Polygon;

          const latLngs = polygonLayer.getLatLngs();
          const polygonCoordinates = (latLngs as L.LatLng[][])[0];

          const shapeArea: number =
            L.GeometryUtil.geodesicArea(polygonCoordinates);

          const vertexCount = polygonCoordinates.length;

          // 実際に検索するデータソースがゼンリンのときのみ上限を100000㎡に絞る
          // （背景がゼンリンでも TRUSTART/GEOSPACE を引く場合があるため、背景モードではなく
          //   searchSource で判定する。GEOSPACE/TRUSTART は従来どおり160000㎡）
          const maxSearchArea =
            searchSource.id === "zenrin"
              ? MAX_SEARCH_AREA_SQUARE_METERS_ZENRIN
              : MAX_SEARCH_AREA_SQUARE_METERS;

          if (vertexCount <= MAX_SEARCH_VERTEX) {
            if (shapeArea <= maxSearchArea) {
              const polygon: string = polygonCoordinates
                .map(({ lng, lat }: L.LatLng) => `${lng},${lat}`)
                .concat(
                  `${polygonCoordinates[0].lng},${polygonCoordinates[0].lat}`
                )
                .join(",");
              await fetchPolygonData({ polygon });
            } else {
              drawnItemsRef.current.removeLayer(layer);
              toast.info(
                `図形は${formatNumberWithComma(
                  maxSearchArea
                )}㎡以内で描画してください`
              );
            }
          } else {
            drawnItemsRef.current.removeLayer(layer);
            toast.info("図形は十角形以内で描画してください");
          }
        }
      } finally {
        // 自分が登録したcontrollerのままなら解放する
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null;
        }
      }
    };
    await processLayer().catch(() => {
      drawnItemsRef.current.removeLayer(layer);
      toast.error("データの取得中にエラーが発生しました。");
    });
  };

  // 取得した地番データをモーダルのテーブルに反映
  useEffect(() => {
    const processProperties = (): void => {
      if (!geospaceReverseByShapeResult) return;

      geospaceReverseByShapeResult.forEach((result: GeoSpaceChiban) => {
        // 日本語の文字またはアルファベットを含む場合はスキップ（CHIBAN_FULL_PATTERNは除く）
        if (skipSelectChiban(result.chiban)) {
          return;
        }

        setNewProperty((prev) => {
          const bookType = "LAND" as BookTypeEng;

          const lastId: number = prev.length > 0 ? prev[prev.length - 1].id : 0;
          return [
            ...prev,
            {
              id: lastId + 1,
              selected: true,
              bookType,
              prefName: result.prefName,
              location: buildLocationString(result),
              chibanKaokuNumber: result.chiban,
              area: result.area,
              isFeedOrigin: false,
              rawData: result,
            },
          ].sort((a, b) => a.id - b.id);
        });
      });
      toast.dismiss(toastId ?? undefined);
      toast.info("選択範囲のデータ取得が完了しました");
    };
    processProperties();
  }, [geospaceReverseByShapeResult]);

  // Leaflet Drawで円を描画する際に、コンポーネント内で起きるtypeerrorを表示しない
  useEffect(() => {
    // マウント時の処理
    const originalOnError = window.onerror;

    window.onerror = function (message) {
      if (
        typeof message === "string" &&
        message.includes("toFixed is not a function")
      ) {
        // エラーを無視し、コンソールへの出力を抑制
        return true;
      }
      return false; // デフォルトのエラーハンドリングを続行
    };

    // アンマウント時の処理（クリーンアップ）
    return () => {
      // window.onerror を元のハンドラに戻す
      window.onerror = originalOnError;
    };
  }, []);

  // 円と多角形に関連する部分のみ日本語化
  useEffect(() => {
    Object.assign(L.drawLocal.draw.toolbar.buttons, {
      circle: "円を描画して、範囲選択",
      polygon: "多角形を描画して、範囲選択",
    });

    Object.assign(L.drawLocal.draw.handlers.circle.tooltip, {
      start: "ドラッグしてして円を描画",
      line: "ドラッグして円の半径を設定",
      end: "マウスを離して円の描画を完了",
    });

    Object.assign(L.drawLocal.draw.handlers.circle, {
      radius: "半径",
    });

    Object.assign(L.drawLocal.draw.handlers.polygon, {
      area: "面積",
    });

    Object.assign(L.drawLocal.draw.toolbar.actions, {
      title: "描画をキャンセル",
      text: "キャンセル",
    });

    Object.assign(L.drawLocal.draw.toolbar.finish, {
      title: "描画を完了",
      text: "完了",
    });

    Object.assign(L.drawLocal.draw.handlers.polygon.tooltip, {
      start: "クリックして描画を開始",
      cont: "クリックして頂点を追加",
      end: "最初のポイントをクリックして多角形を閉じる",
    });

    Object.assign(L.drawLocal.draw.toolbar.undo, {
      title: "最後のポイントを削除",
      text: "最後のポイントを削除",
    });
    Object.assign(L.drawLocal.draw.handlers.polyline, {
      error: "三角形以上で描画してください",
    });

    Object.assign(L.drawLocal.draw.handlers.simpleshape.tooltip, {
      end: "マウスを離して円の描画を完了",
    });
  }, []);

  // 地図出力パネルを開いている間は、ゼンリン / GEOSPACE セクションの操作を抑止する
  const lockedGroupTitles = mapExportPanelOpen
    ? [ZENRIN_SECTION_TITLE, GEOSPACE_SECTION_TITLE]
    : undefined;

  // LayerGroupControlでセクション別（ゼンリン/GEOSPACE/その他）にチェックボックスを表示するためグループ化
  const overlayGroups: OverlayGroup[] = getOverlayGroups({
    // TRUSTART
    trustartChibanMap: Boolean(trustartChibanMap),
    registrationMapAvailable: showRegistrationMapInPanel,
    // 役所地番地図・登記所備付地図レイヤーを利用できるテナントか（暫定ゲート）
    isMunicipalParcelMapAvailable,
    // 道路
    isRoadLayerEnabled: Boolean(isRoadLayerEnabled),
    // ゼンリンセクションを表示するかどうか
    showZenrinSection,
    // ゼンリン地図の利用権限があるか
    canUseZenrinMap,
    // GEOSPACEセクションを表示するかどうか
    showGeospaceSection,
    // GEOSPACEの電子地図の契約があるか
    cdsMap: Boolean(cdsMap),
    // NTTインフラネット地図の認証用AppID
    mapServiceAppId,
    // 役所資料（黒塗り）のレイヤーは reassist-archive フラグが true のときだけ出す
    reassistArchive: reassistArchive === true,
    // 受付帳のピンが1つ以上登録されているか
    hasReceptionBookMarkers,
    // 保存した物件リストのピンをリストごとに色分けしているか（CRM の時だけ渡される）
    savedPlotColorsByList: savedPlotColorByRequestId !== undefined,
    // 用途地域（都市計画決定GIS）の有効フラグ
    cityPlanningAreaUsePurposeLayer: Boolean(cityPlanningAreaUsePurposeLayer),
    // 遊休農地表示機能の有効フラグ
    idolFarmland: Boolean(idolFarmland),
    // 農地表示機能の有効フラグ
    farmland: Boolean(farmland),
    // 農地レイヤーが有効な場合、その条件設定ダイアログを開くアクション構成
    farmlandAction: farmland
      ? {
          label: "農地の条件設定",
          icon: <TuneIcon fontSize="small" />,
          active: farmlandFilterActive,
          disabled: !overlayVisibility[FARMLAND_LAYER_NAME],
          onClick: () => {
            setFarmlandFilterDialogOpen(true);
          },
        }
      : undefined,
  });

  // 乖離リカバリーで ON にするレイヤーが、ON にしたとき実際に描画されるか。
  // レイヤーパネルで操作できても、タイル URL・トークン未取得の間はタイルがマウントされない
  // ため、下の JSX のマウント条件と同じ条件を見る。
  const isRecoveryLayerRenderable: Record<string, boolean> = {
    [GEOSPACE_CHIBAN_TILE_LAYER_NAME]:
      showGeospaceSection &&
      Boolean(hasMapViewingPermission) &&
      Boolean(mapServiceAppId),
    [LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME]:
      showGeospaceSection &&
      Boolean(hasMapViewingPermission) &&
      Boolean(mapServiceAppId),
    [TRUSTART_CHIBAN_LAYER_NAME]:
      Boolean(trustartChibanMap) &&
      isMunicipalParcelMapAvailable &&
      trustartPmtilesUrl !== undefined,
    [REGISTRATION_MAP_LAYER_NAME]: canRenderRegistrationMap,
    [ZENRIN_BLUE_MAP_LAYER_NAME]:
      showZenrinSection &&
      Boolean(canUseZenrinMap) &&
      Boolean(zenrinOAuthToken),
  };
  // 乖離リカバリーの「表示」ボタンは、確実に切り替えられるときだけ出す
  const canShowOverlay = (name: string): boolean =>
    isOverlayToggleable({ overlayGroups, lockedGroupTitles, name }) &&
    (isRecoveryLayerRenderable[name] ?? true);
  canShowOverlayRef.current = canShowOverlay;

  return (
    <>
      <DebugView visible={debugVisible} />
      <MapContainer
        center={viewLatLng}
        zoom={INITIAL_ZOOM}
        scrollWheelZoom={true}
        style={containerStyle}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        maxBounds={
          isGeospaceElectronicMapSelected && geospaceLatLngBounds != null
            ? geospaceLatLngBounds
            : undefined
        }
        ref={mapRefCallback}
      >
        <ScaleControl
          position="bottomleft"
          metric={true}
          imperial={false}
          maxWidth={200}
          updateWhenIdle={true}
        />
        {/* 左下ではスケールバーを上、中心座標/ズーム表示を地図左下端に配置する */}
        <MapCenterControl />
        {/* スマホ幅では標準の出典欄を隠し、情報ボタン＋ダイアログで出典を表示する */}
        <MapAttributionControl />
        {isGeospaceElectronicMapSelected && geospaceLatLngBounds != null && (
          <Rectangle
            bounds={geospaceLatLngBounds}
            pathOptions={{ color: "#2d7ff9", weight: 2, fill: false }}
          />
        )}
        {/* 印刷出力範囲のプレビュー枠 */}
        {printFramePreview && (
          <PrintFrameRectangle printOptions={printFramePreview} />
        )}
        {/* ユーザーが取得する住宅地図画像の範囲を視覚的に確認できるようゼンリン画像選択モード時にA3範囲の矩形を表示 */}
        {zenrinSelectionBounds && (
          <Rectangle
            bounds={zenrinSelectionBounds}
            pathOptions={{
              color: "#2e7d32",
              weight: 2,
              dashArray: "6 4",
              fill: false,
            }}
            interactive={false}
          />
        )}
        {/*
          ゼンリン取得画像をtilePaneに配置することで、
          他のタイルレイヤーと同じpane内でz-indexを統一的に管理する
        */}
        {zenrinImageOverlay &&
          overlayVisibility[ZENRIN_FETCHED_IMAGE_LAYER_NAME] && (
            <ImageOverlay
              bounds={zenrinImageOverlay.bounds}
              url={zenrinImageOverlay.url}
              opacity={1}
              interactive={false}
              pane="tilePane"
              zIndex={LAYER_Z_INDEX.ZENRIN_FETCHED_IMAGE}
              attribution="Copyright © ZENRIN CO., LTD. All Rights Reserved."
            />
          )}
        <MapInitializer setMapInstance={setMap} />
        <CircleRadiusDisplay />

        {/* オーバーレイレイヤーのセクション別グループ化を実現するための独自レイヤーコントロール */}
        <LayerGroupControl
          overlayGroups={overlayGroups}
          overlayVisibility={overlayVisibility}
          onToggleOverlay={handleToggleOverlay}
          layerBadges={photoCoverageBadges}
          yearAvailability={layerControlYearAvailability}
          onYearListToggle={handleYearListToggle}
          choiceValues={layerControlChoiceValues}
          onSelectChoice={handleSelectLayerControlChoice}
          lockedGroupTitles={lockedGroupTitles}
        />
        <CFarmlandFilterDialog
          open={farmlandFilterDialogOpen}
          initialFilter={farmlandFilter}
          onClose={() => {
            setFarmlandFilterDialogOpen(false);
          }}
          onApply={setFarmlandFilter}
        />

        {!disableOpenStreetMap && (
          <TileLayer
            ref={osmLayerRef}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxNativeZoom={OPEN_STREET_MAP_MAX_NATIVE_ZOOM}
            maxZoom={MAX_ZOOM}
          />
        )}
        <FeatureGroup ref={drawnItemsRef}>
          <EditControl
            position="topleft"
            onCreated={handleDrawCreated}
            draw={{
              polygon: {
                showLength: true,
                shapeOptions: {
                  stroke: true,
                  color: "#ff0000",
                  weight: 5,
                  opacity: 0.5,
                  fill: true,
                  fillColor: "#ffcccc",
                  fillOpacity: 0.5,
                  clickable: true,
                  pane: DRAWN_SHAPES_PANE_NAME,
                },
              },
              circle: {
                shapeOptions: {
                  stroke: true,
                  color: "#ff0000",
                  weight: 5,
                  opacity: 0.5,
                  fill: true,
                  fillColor: "#ffcccc",
                  fillOpacity: 0.5,
                  // react-leaflet-drawの型定義はleaflet-drawのものであり、clickableからinteractiveに移行済み
                  // しかし、react-leaflet-drawが実装に使用しているleaflet-drawはclickableからinteractiveに移行していない
                  // そのためclickableを使用するが、型エラーが発生するため意図的にエラーを無視するように実装
                  // @ts-expect-error: 上記の理由により型エラーを許容
                  clickable: true,
                  pane: DRAWN_SHAPES_PANE_NAME,
                },
              },
              marker: false,
              polyline: false,
              rectangle: false,
              circlemarker: false,
            }}
            edit={{
              edit: false,
              remove: false,
            }}
          />
        </FeatureGroup>
        <FitToObjectsControl map={map} drawnItemsRef={drawnItemsRef} />
        <CurrentLocationControl position="topleft" />
        {showGeospaceSection &&
          cdsMap &&
          overlayVisibility[GEOSPACE_ELECTRONIC_MAP_LAYER_NAME] &&
          mapServiceAppId && (
            <CdsTileLayerWithHook
              setDisableOpenStreetMap={setDisableOpenStreetMap}
              onAuthorizeKeyResponse={handleAuthorizeKeyResponse}
              attribution={"Copyright© NTTインフラネット. All Rights Reserved."}
              url={
                "https://cds.geospace.jp/images/wmts/1.0.0/road/default/{authorizeKey}/EPSG3857/{z}/{y}/{x}.png"
              }
              maxNativeZoom={21}
              maxZoom={MAX_ZOOM}
              minZoom={MIN_ZOOM}
            />
          )}
        {showGeospaceSection && hasMapViewingPermission && mapServiceAppId && (
          <CommonChibanTileLayer
            mapServiceAppId={mapServiceAppId}
            checkedGeospace={overlayVisibility[GEOSPACE_CHIBAN_TILE_LAYER_NAME]}
            checkedLegalAffairs={
              overlayVisibility[LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME]
            }
          />
        )}
        {/* TRUSTART地番図（フィーチャーフラグ ON かつ URL 取得後のみマウント）。
            暫定: 対象テナント（isMunicipalParcelMapAvailable）のみ表示する。 */}
        {trustartChibanMap &&
          isMunicipalParcelMapAvailable &&
          trustartPmtilesUrl !== undefined && (
            <TrustartChibanTileLayer
              pmtilesUrl={trustartPmtilesUrl}
              visible={Boolean(overlayVisibility[TRUSTART_CHIBAN_LAYER_NAME])}
            />
          )}
        {/* 全国最新写真（国土地理院）。ベースマップより上、他レイヤーより下に描画 */}
        <SeamlessPhotoTileLayer
          visible={Boolean(overlayVisibility[SEAMLESS_PHOTO_LAYER_NAME])}
        />
        {/* 年代別の写真（国土地理院）。全国最新写真と同じ層に重ねる */}
        <DatedPhotoLayers
          overlayVisibility={overlayVisibility}
          onCoverageChange={handlePhotoCoverageChange}
        />
        <PhotoCoverageNotice items={photoCoverageNoticeItems} />
        <MaplibreLayerContext zIndex={LAYER_Z_INDEX.MAPLIBRE}>
          {renderableRegistrationMapUrl !== undefined ? (
            <RegistrationMapTileLayer
              pmtilesUrl={renderableRegistrationMapUrl}
              visible={Boolean(overlayVisibility[REGISTRATION_MAP_LAYER_NAME])}
              onParcelClick={handleRegistrationParcelClick}
            />
          ) : null}
          <AreaUsePurposeLayerControlWrapper
            defaultVisible={
              overlayVisibility[
                AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION
              ] ?? false
            }
            visible={
              overlayVisibility[
                AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION
              ] ?? false
            }
            initialZoomLevel={INITIAL_ZOOM}
            tileHost={NATIONAL_LAND_NUMERICAL_INFORMATION_HOST}
            layerName={
              AREA_USE_PURPOSE_LAYER_NAME_NATIONAL_LAND_NUMERICAL_INFORMATION
            }
            attribution='<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>'
          />
          {cityPlanningAreaUsePurposeLayer && (
            <AreaUsePurposeLayerControlWrapper
              defaultVisible={
                overlayVisibility[
                  AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA
                ] ?? false
              }
              visible={
                overlayVisibility[
                  AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA
                ] ?? false
              }
              initialZoomLevel={INITIAL_ZOOM}
              tileHost={CITY_PLANNING_DECISION_GIS_DATA_HOST}
              layerName={
                AREA_USE_PURPOSE_LAYER_NAME_CITY_PLANNING_DECISION_GIS_DATA
              }
              attribution="都市計画決定GISデータ"
            />
          )}
          <LandPriceLayerControlWrapper
            defaultVisible={overlayVisibility[LAND_PRICE_LAYER_NAME] ?? false}
            visible={overlayVisibility[LAND_PRICE_LAYER_NAME] ?? false}
            initialZoomLevel={INITIAL_ZOOM}
          />
        </MaplibreLayerContext>
        <TradePriceLayerControlWrapper
          defaultVisible={overlayVisibility[TRADE_PRICE_LAYER_NAME] ?? false}
          visible={overlayVisibility[TRADE_PRICE_LAYER_NAME] ?? false}
          initialZoomLevel={INITIAL_ZOOM}
          filter={tradePriceFilter}
          onFilterChange={onTradePriceFilterChange}
          onShowDetailList={onShowTradePriceDetailList}
        />
        <YearSelectableLayer
          name={ELEMENTARY_SCHOOL_AREA_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => (
            <SchoolAreaLayer
              defaultVisible={defaultVisible}
              visible={visible}
              layerName={ELEMENTARY_SCHOOL_AREA_LAYER_NAME}
              dataLayerName={getEsSchoolAreaDataLayerName(year)}
              layerUrl={getEsSchoolAreaTileUrl(
                year,
                process.env.VITE_COMMON_TILE_HOST ?? ""
              )}
              schoolType={SchoolType.ES}
              polygonStrokeColor={"#8780ff"}
              circleFillColor={"#FFD54F"}
            />
          )}
        />
        <YearSelectableLayer
          name={JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => (
            <SchoolAreaLayer
              defaultVisible={defaultVisible}
              visible={visible}
              layerName={JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME}
              dataLayerName={getJhSchoolAreaDataLayerName(year)}
              layerUrl={getJhSchoolAreaTileUrl(
                year,
                process.env.VITE_COMMON_TILE_HOST ?? ""
              )}
              schoolType={SchoolType.JHS}
              polygonStrokeColor={"#6d26ff"}
              circleFillColor={"#ffa347"}
            />
          )}
        />
        {isRoadLayerEnabled && ROAD_TILE_HOST && (
          <RoadLayer
            visible={overlayVisibility[ROAD_LAYER_NAME]}
            initialZoomLevel={INITIAL_ZOOM}
            mode={roadLayerMode}
          />
        )}
        <YearSelectableLayer
          name={FLOOD_10_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardFlood10Layer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <YearSelectableLayer
          name={FLOOD_20_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardFlood20Layer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <YearSelectableLayer
          name={FLOOD_30_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardFlood30Layer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <HazardTsunamiLayer
          defaultVisible={overlayVisibility[TSUNAMI_LAYER_NAME]}
          visible={overlayVisibility[TSUNAMI_LAYER_NAME]}
        />
        <HazardStormSurgeLayer
          defaultVisible={overlayVisibility[STORM_SURGE_LAYER_NAME]}
          visible={overlayVisibility[STORM_SURGE_LAYER_NAME]}
        />
        <YearSelectableLayer
          name={LANDSLIDE_STEEP_SLOPE_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardLandslideSteepSlopeLayer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <YearSelectableLayer
          name={LANDSLIDE_DEBRIS_FLOW_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardLandslideDebrisFlowLayer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <YearSelectableLayer
          name={LANDSLIDE_LANDSLIDE_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <HazardLandslideLandslideLayer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <YearSelectableLayer
          name={LANDSLIDE_PREVENTION_LAYER_NAME}
          overlayVisibility={overlayVisibility}
          render={(year, defaultVisible, visible) => {
            return (
              <LandslidePreventionLayer
                year={year}
                defaultVisible={defaultVisible}
                visible={visible}
              />
            );
          }}
        />
        <SavedPlotLayer
          data={savedPlotData}
          colorByRequestId={savedPlotColorByRequestId}
          defaultVisible={overlayVisibility[SAVED_PLOT_LAYER_NAME]}
          visible={overlayVisibility[SAVED_PLOT_LAYER_NAME]}
          savedMapPlotData={savedMapPlotData}
          setSelectedProperties={setSelectedProperties}
          setCurrentPopupRowId={setCurrentPopupRowId}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag}
          handleKaokuNumberSearch={handleKaokuNumberSearch}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
          handleRealEstateDocumentFetchModal={
            handleRealEstateDocumentFetchModal
          }
          handleRealEstateResearchRequestModal={
            handleRealEstateResearchRequestModal
          }
          propertySummary={propertySummary}
          handleNewPropertySheet={handleNewPropertySheet}
        />
        <RequestedPicturesPlotLayer
          defaultVisible={overlayVisibility[REQUESTED_PICTURES_PLOT_LAYER_NAME]}
          visible={overlayVisibility[REQUESTED_PICTURES_PLOT_LAYER_NAME]}
          handlePinSave={handlePinSave}
          setSelectedProperties={setSelectedProperties}
          selectedProperties={selectedProperties}
          checkDuplicateProperties={checkDuplicateProperties}
          onTagsChange={onRequestedPicturesTagsChange}
          onRemarksChange={onRequestedPicturesRemarksChange}
          onAdditionalFieldsChange={onRequestedPicturesAdditionalFieldsChange}
          setCurrentPopupRowId={setCurrentPopupRowId}
        />
        <RealEstateSearchLayer
          defaultVisible={overlayVisibility[REAL_ESTATE_SEARCH_LAYER_NAME]}
          visible={overlayVisibility[REAL_ESTATE_SEARCH_LAYER_NAME]}
          realEstateSearchData={realEstateSearchResult}
          handleKaokuNumberSearch={handleKaokuNumberSearch}
          kaokuNumberSearchFlag={Boolean(kaokuNumberSearch)}
          onTagsChange={onRealEstateSearchTagsChange}
          onRemarksChange={onRealEstateSearchRemarksChange}
          onAdditionalFieldsChange={onRealEstateSearchAdditionalFieldsChange}
          onPinClick={onRealEstateSearchPinClick}
          loadLandAddress={loadRealEstateSearchLandAddress}
          handlePinSave={handlePinSave}
          crm={crm}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
          handleRealEstateDocumentFetchModal={
            handleRealEstateDocumentFetchModal
          }
          handleRealEstateResearchRequestModal={
            handleRealEstateResearchRequestModal
          }
          propertySummary={propertySummary}
          handleNewPropertySheet={handleNewPropertySheet}
        />
        {/* ゼンリン契約ユーザー向けに住宅地図を表示するためのタイルレイヤー */}
        {showZenrinSection &&
          overlayVisibility[ZENRIN_RESIDENTIAL_MAP_LAYER_NAME] &&
          canUseZenrinMap && (
            <ZenrinTileLayer
              id={"zenrin-tile-layer"}
              canUseZenrinMap={canUseZenrinMap}
              oauthToken={zenrinOAuthToken}
              setDisableOpenStreetMap={setDisableOpenStreetMap}
              attribution={"Copyright © ZENRIN CO., LTD. All Rights Reserved."}
              url={`${ZENRIN_BASE_URL}/map/wmts_tile/w2nhkFRc/default/Z3857_3_21/{z}/{y}/{x}.png`}
              maxNativeZoom={22}
              // minNativeZoom={3}
              maxZoom={ZENRIN_RESIDENTIAL_MAX_ZOOM}
              minZoom={ZENRIN_RESIDENTIAL_MIN_ZOOM}
              zIndex={LAYER_Z_INDEX.ZENRIN_RESIDENTIAL}
            />
          )}
        {/* 法務局地番情報付きの地図を表示するゼンリンブルーマップレイヤー（ズーム18以上でのみ表示） */}
        {showZenrinSection &&
          overlayVisibility[ZENRIN_BLUE_MAP_LAYER_NAME] &&
          canUseZenrinMap &&
          zenrinOAuthToken && (
            <ZenrinBlueMapTileLayer
              oauthToken={zenrinOAuthToken}
              minZoom={ZENRIN_BLUE_MAP_MIN_ZOOM}
              zIndex={LAYER_Z_INDEX.ZENRIN_BLUE_MAP}
              onTokenInvalid={handleZenrinTokenInvalid}
              attribution="Copyright © ZENRIN CO., LTD. All Rights Reserved."
            />
          )}
        {idolFarmland && (
          <IdolFarmlandLayer
            defaultVisible={overlayVisibility[IDOL_FARMLAND_LAYER_NAME]}
            visible={overlayVisibility[IDOL_FARMLAND_LAYER_NAME]}
          />
        )}
        {farmland && (
          <FarmlandLayer
            visible={overlayVisibility[FARMLAND_LAYER_NAME]}
            filter={farmlandFilter}
          />
        )}
        <CityPlanningAreaLayer
          defaultVisible={overlayVisibility[CITY_PLANNING_AREA_LAYER_NAME]}
          visible={overlayVisibility[CITY_PLANNING_AREA_LAYER_NAME]}
        />
        <FireProtectionAreaLayer
          defaultVisible={overlayVisibility[FIRE_PROTECTION_AREA_LAYER_NAME]}
          visible={overlayVisibility[FIRE_PROTECTION_AREA_LAYER_NAME]}
        />
        <HighLevelAreaLayer
          defaultVisible={overlayVisibility[HIGH_LEVEL_AREA_LAYER_NAME]}
          visible={overlayVisibility[HIGH_LEVEL_AREA_LAYER_NAME]}
        />
        <HighLevelUseAreaLayer
          defaultVisible={overlayVisibility[HIGH_LEVEL_USE_AREA_LAYER_NAME]}
          visible={overlayVisibility[HIGH_LEVEL_USE_AREA_LAYER_NAME]}
        />
        <DisasterRiskAreaLayer
          defaultVisible={overlayVisibility[DISASTER_RISK_AREA_LAYER_NAME]}
          visible={overlayVisibility[DISASTER_RISK_AREA_LAYER_NAME]}
        />
        {reassistArchive === true &&
          overlayVisibility[MUNICIPAL_DOCUMENT_LAYER_NAME] && (
            <MunicipalDocumentLayer />
          )}
        {overlayVisibility[RECEPTION_BOOK_LAYER_NAME] && (
          <ReceptionBookLayer
            ref={receptionBookLayerRef}
            markerStates={receptionBookMarkerStates ?? []}
            crm={crm}
            selectedProperties={selectedProperties}
            onTagsChange={onReceptionBookTagsChange}
            onRemarksChange={onReceptionBookRemarksChange}
            onAdditionalFieldsChange={onReceptionBookAdditionalFieldsChange}
            onReceptionBookPinClick={(latlng) => {
              // 受付帳ピンでgoogle mapsや地価マップを開くための処理
              setClickedLatLng(latlng);
            }}
            handlePinSave={handlePinSave}
            availableFields={availableFields}
            hasAvailableFields={hasAvailableFields}
            kaokuNumberSearchFlag={kaokuNumberSearchFlag}
            handleKaokuNumberSearch={handleKaokuNumberSearch}
            realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
            handleRealEstateDocumentFetchModal={
              handleRealEstateDocumentFetchModal
            }
            handleRealEstateResearchRequestModal={
              handleRealEstateResearchRequestModal
            }
            propertySummary={propertySummary}
            handleNewPropertySheet={handleNewPropertySheet}
          />
        )}
        <CustomControl prepend={true} position="bottomright">
          <LegendsControl registrationMapAvailable={canRenderRegistrationMap} />
        </CustomControl>
        {!markerState.hide && map && (
          // 情報が出た地点は物件一覧に入るので一覧と同じ青、出なかった地点は小さな灰色の丸。
          // ポップアップを開いている間は選択中の見た目になる
          <CanvasPin
            position={markerState.latlng ?? viewLatLng}
            icon={
              markerState.content
                ? getTeardropPinIcon(PIN_COLORS.listed)
                : getPlainClickPinIcon()
            }
            selectedIcon={
              markerState.content
                ? getTeardropPinIcon(PIN_COLORS.listed, { selected: true })
                : undefined
            }
            drawLevel={PIN_DRAW_LEVEL.listed}
            renderer={getSharedCanvasPinRenderer(map)}
            eventHandlers={createMarkerEventHandlers(
              markerState,
              setClickedLatLng,
              onMapClickTagsUpdate,
              onMapClickRemarksUpdate,
              onMapClickAdditionalFieldsUpdate,
              currentPopupIdentifier
            )}
          >
            {map && (
              // 小さい画面のデバイス(iPhone SE等)でもポップアップが正しく動作するように、最大幅を地図コンテナ幅の80%に設定している
              <Popup
                ref={popupRef}
                maxWidth={map ? Math.floor(map.getSize().x * 0.8) : undefined}
                // 検索で中央へ移動したピンのときだけ、高さを中央のピンの上に収まる範囲に
                // 抑え、autoPanで地図がずれる（＝検索した地点が中央からずれる）のを防ぐ
                maxHeight={
                  isSearchCenteredPin
                    ? calcPopupMaxHeight(map.getSize().y)
                    : undefined
                }
              >
                {markerState.content}
              </Popup>
            )}
          </CanvasPin>
        )}
        {/* 物件一覧に入っている物件を青ピンで示す（元のピンの上に重ね、一覧から消すと元の色に戻る） */}
        <ListedPropertyPinsLayer
          positions={selectedPropertiesPinData}
          focusedPosition={focusedPropertyPinPosition}
          onPinClick={onListedPropertyPinClick}
        />
        {polygonData && polygonData.length > 0 && (
          <ParcelPolygon positions={polygonData} />
        )}
        {clickedAreaPolygonData && clickedAreaPolygonData.length > 0 && (
          <ParcelPolygon
            positions={clickedAreaPolygonData}
            pathOptions={{ color: "green", fill: false }}
          />
        )}
        <Modal
          open={RangeSelectModalIsOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              // SP は画面いっぱい（一覧・ボタンが横にはみ出さないように）。PC は従来どおり
              width: { xs: "100%", [MOBILE_BREAKPOINT]: "60%" },
              height: { xs: "100%", [MOBILE_BREAKPOINT]: "auto" },
              boxShadow: 24,
              borderRadius: { xs: 0, [MOBILE_BREAKPOINT]: 2 },
              outline: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <RangeSelectResultModal
              rows={newProperty}
              setRows={setNewProperty}
              setSelectedProperties={setSelectedProperties}
              setRangeSelectModalIsOpen={setRangeSelectModalIsOpen}
              buildLocationString={buildLocationString}
              findDuplicatePropertyIndex={findDuplicatePropertyIndex}
              mergePropertySelectionRow={mergePropertySelectionRow}
              geospaceReverseByShapeResult={geospaceReverseByShapeResult}
              resultLimit={
                rangeSearchSourceId
                  ? RANGE_SEARCH_RESULT_LIMIT[rangeSearchSourceId]
                  : undefined
              }
              truncated={rangeSearchTruncated}
              sourceLabel={
                rangeSearchSourceId
                  ? SEARCH_SOURCE_LABEL[rangeSearchSourceId]
                  : undefined
              }
              onShowSourceLayer={
                rangeSearchSource?.tier === 2 &&
                canShowOverlay(
                  SEARCH_SOURCE_LAYER[rangeSearchSource.id].layerName
                )
                  ? () => {
                      handleToggleOverlay(
                        SEARCH_SOURCE_LAYER[rangeSearchSource.id].layerName,
                        true
                      );
                    }
                  : undefined
              }
            />
          </Box>
        </Modal>
      </MapContainer>
    </>
  );
};
